// @flow
/* eslint-disable max-lines-per-function, react/require-default-props, max-lines */
import moment from 'moment'
import * as React from 'react'
import { connect } from 'react-redux'

import Accessibility from '@/accessibility'
import { Button, Calendar, DropDown, DropdownQuantity, FullPageModal } from '@/components'
import { ageOptions } from '@/components/search-bar'
import type { Props as SearchBarProps } from '@/components/search-bar'
import Strings, {
  DISPLAY_DATE_FORMAT,
  DEFAULT_NUMBER_NIGHTS,
  MIN_NUMBER_NIGHTS,
  MAX_NUMBER_NIGHTS,
  Images,
  Routes,
} from '@/constants'
import { classnames, objectToBase64, base64ToObject, dataFromQueryString } from '@/helpers'
import history from '@/helpers/history'
import { Property } from '@/models';
import selectors from '@/selectors'
import { ActionCreators } from '@/redux'

import { SEARCH_BAR_FIELD, VALIDATION } from '../search-bar/constants'
import styles from './styles.scss'

const { Search } = Accessibility

const DATE = 'date'
const TRAVELERS = 'travelers'
const FILTERS = 'filters'

type DispatchToProps = {|
  fetchProperty: (data: SearchPayload) => void,
  setFilterProperty: (data: any) => void,
|}

type Props = {
  ...SearchBarProps,
  ...DispatchToProps,
  title?: string,
  goBack?: boolean,
  // showCrumbs?: boolean,
  filters?: React.Node,
  filterCount?: number,
  properties: {
    values: Array<Property>,
    isPending: boolean,
    errors: ?ResponseError
  },
  isNewDestination?: boolean,
  onNewDestination?: (data: boolean) => void,
}

const TopBar = ({
  locations, properties, match, search, goBack, title = '', filters = null, filterCount = 0, fetchProperty, setFilterProperty, isNewDestination, onNewDestination
}: Props) => {
  const { code } = match.params
  const [fromDate, setFromDate] = React.useState(moment().add(1, 'M').format(DISPLAY_DATE_FORMAT))
  const [toDate, setToDate] = React.useState(moment().add(1, 'M').add(DEFAULT_NUMBER_NIGHTS, 'd').format(DISPLAY_DATE_FORMAT))
  const [adults, setAdults] = React.useState(2)
  const [children, setChildren] = React.useState<Array<number>>([])
  const [destination, setDestination] = React.useState<string | null>(null)
  const [errors, setErrors] = React.useState({
    fromDate: false,
    toDate: false,
    adults: false,
    children: false,
    destination: false
  })
  const [visible, setVisible] = React.useState(false)
  const [content, setContent] = React.useState(null)
  const [modalTitle, setModalTitle] = React.useState('')

  React.useEffect(() => {
    const { values: propertyValues } = properties
    const { search: query } = dataFromQueryString(search)
    const data = base64ToObject(query)
    const {
      fromDate: searchFromDate,
      toDate: searchToDate,
      adults: searchAdults,
      children: searchChildren,
      destination: searchDestination
    } = data
    if (searchFromDate) setFromDate(searchFromDate)
    if (searchToDate) setToDate(searchToDate)
    if (searchAdults) setAdults(searchAdults)
    if (searchChildren) setChildren(searchChildren)
    if (searchDestination && searchDestination.length > 0) setDestination(searchDestination[0])
    else if (propertyValues.length > 0 && propertyValues[0].region) {
      setDestination(`${propertyValues[0].region}`)
    }
  }, [search, properties])

  React.useEffect(() => {
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'days') > moment(toDate, DISPLAY_DATE_FORMAT)) {
      setToDate(moment(fromDate, DISPLAY_DATE_FORMAT).add(DEFAULT_NUMBER_NIGHTS, 'days').format(DISPLAY_DATE_FORMAT))
    }
  }, [fromDate])
  
  const validateFields = () => {
    const checks = {
      fromDate: VALIDATION.fromDate(fromDate)
        // $FlowFixMe
        || moment().add(7, 'd') > moment(fromDate, DISPLAY_DATE_FORMAT),
      toDate: VALIDATION.toDate(toDate)
        // $FlowFixMe
        || moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'd') > moment(toDate, DISPLAY_DATE_FORMAT)
        // $FlowFixMe
        || moment(fromDate, DISPLAY_DATE_FORMAT).add(MAX_NUMBER_NIGHTS, 'd') < moment(toDate, DISPLAY_DATE_FORMAT),
      adults: VALIDATION.adults(adults),
      children: VALIDATION.children(children),
      destination: VALIDATION.destination(destination)
    }
    setErrors(checks)
    return Object.values(checks).every(e => !e)
  }

  const onSearch = () => {
    if (validateFields()) {
      const data = { fromDate, toDate, adults, children, destination: [destination] }
      if (isNewDestination) {
        setFilterProperty(null)
      }

      if (code && !isNewDestination) {
        // $FlowFixMe
        fetchProperty({ ...data, code })
        history.push({
          pathname: Routes.HotelInfo.replace(':code', code),
          search: `search=${objectToBase64({ ...data, code })}&hash=${Math.random()}`
        })
      } else {
        history.push({
          pathname: Routes.SearchResults,
          search: `search=${objectToBase64({ ...data })}&hash=${Math.random()}`
        })
      }
      if (onNewDestination) {
        onNewDestination(false)
      }
      setVisible(false)
    }
  }

  const getOption = (value: string | null, options: OptionsType<string>) => {
    const [option] = options.filter(opt => opt.value === value)
    return option
  }

  const onDestinationChange = (city: {label: string, value: string}) => {
    if (onNewDestination) {
      onNewDestination(true)
    }
    setDestination(city.value)
  }
  const onFromDateChange = (date: string) => setFromDate(date)
  const onToDateChange = (date: string) => setToDate(date)
  const onAdultsChange = (number: number) => setAdults(number)
  const onChildrenChange = (number: number) => setChildren([...children, 1, 1, 1, 1, 1].slice(0, number))
  const onChangeChildAge = (idx, option: {label: string, value: string}) => {
    const updated = [...children]
    updated.splice(idx, 1, parseInt(option.value, 10))
    setChildren(updated)
  }

  const closeModal = () => {
    setModalTitle('')
    setVisible(!visible)
  }

  const openModal = (contentName: string) => {
    // eslint-disable-next-line no-nested-ternary
    const mTitle = contentName === DATE
      ? Strings.itinerary.dates
      : contentName === TRAVELERS
        ? Strings.general.travelers
        : Strings.general.sortAndFilterBy
    setModalTitle(mTitle)
    setContent(contentName)
    setVisible(true)
  }

  const getDateTitle = () => fromDate && toDate
    ? `${moment(fromDate, DISPLAY_DATE_FORMAT).format('MM/DD')} - ${moment(
        toDate,
        DISPLAY_DATE_FORMAT,
      ).format('MM/DD')}`
    : Strings.itinerary.dates

  const getTravelersTitle = () => `${adults + children.length} ${Strings.general.travelers}`

  const getToDateError = (): string => {
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'd') > moment(toDate, DISPLAY_DATE_FORMAT)) {
      return Strings.errors.toDate
    }
    // $FlowFixMe
    if (moment(fromDate, DISPLAY_DATE_FORMAT).add(MAX_NUMBER_NIGHTS, 'd') < moment(toDate, DISPLAY_DATE_FORMAT)) {
      return Strings.errors.stayTooLong
    }

    return Strings.cannotBeLeftBlank
  }

  return (
    <div className={styles.topBar}>
      {
        goBack && (
          <button className={styles.leftCarrot} type="button" onClick={goBack}>
            <img src={Images.arrowLeftWhite} alt="left-carrot" />
          </button>
        )
      }
      <h2 className={styles.title}>
        {title}
      </h2>
      <div className={styles.crumbs}>
        <Button className={styles.button} onClick={() => openModal(DATE)} title={getDateTitle()} />
        <Button className={styles.button} onClick={() => openModal(TRAVELERS)} title={getTravelersTitle()} />
        {
          filters ? (
            <Button
              primary={filterCount > 0}
              className={filterCount > 0 ? classnames(styles.filterButtonSelected, styles.button) : styles.filterButton}
              onClick={() => openModal(FILTERS)}
              title={filterCount > 0 ? filterCount : null}
              imgUrls={{default: filterCount > 0 ? Images.filterWhite : Images.filterGrey, selected: Images.filterWhite}}
            />
          ) : null
        }
      </div>
      <FullPageModal
        title={modalTitle}
        visible={visible}
        requestClose={closeModal}
        bottomButton={
          <Button
            title={Strings.search.search}
            onClick={onSearch}
            ariaLabel={Search.Search}
            className={styles.longButton}
          />
        }
      >
        {
          content === DATE && (
            <>
              <div className={styles.row} key="top-bar-2">
                <DropDown
                  ariaLabel={Search.To}
                  placeholder={Strings.search.to}
                  key={SEARCH_BAR_FIELD.destination}
                  name={SEARCH_BAR_FIELD.destination}
                  options={locations}
                  onChange={onDestinationChange}
                  value={getOption(destination, locations)}
                  label={Strings.search.destination}
                  error={errors[SEARCH_BAR_FIELD.destination] && Strings.cannotBeLeftBlank}
                />
              </div>
              <div className={styles.row} key="top-bar-3">
                <Calendar
                  ariaLabel={Search.FromDate}
                  placeholder={Strings.search.depart}
                  key={SEARCH_BAR_FIELD.fromDate}
                  name={SEARCH_BAR_FIELD.fromDate}
                  onChange={onFromDateChange}
                  value={fromDate}
                  label={Strings.search.departing}
                  minDate={moment().add(7, 'days')}
                  error={errors[SEARCH_BAR_FIELD.fromDate] && Strings.cannotBeLeftBlank}
                />
              </div>
              <div className={styles.row} key="top-bar-4">
                <Calendar
                  ariaLabel={Search.ToDate}
                  placeholder={Strings.search.return}
                  key={SEARCH_BAR_FIELD.toDate}
                  name={SEARCH_BAR_FIELD.toDate}
                  onChange={onToDateChange}
                  value={toDate}
                  label={Strings.search.returning}
                  minDate={moment(fromDate, DISPLAY_DATE_FORMAT).add(MIN_NUMBER_NIGHTS, 'days')}
                  error={errors[SEARCH_BAR_FIELD.toDate] && getToDateError()}
                />
              </div>
            </>
          )
        }
        {
          content === TRAVELERS && (
            <>
              <div className={styles.row} key="top-bar-6">
                <DropdownQuantity
                  ariaLabel={Search.Adult}
                  placeholder={Strings.search.zero}
                  key={SEARCH_BAR_FIELD.adults}
                  name={SEARCH_BAR_FIELD.adults}
                  onChange={onAdultsChange}
                  value={adults}
                  label={Strings.search.adults}
                  error={errors[SEARCH_BAR_FIELD.adults] && Strings.cannotBeLeftBlank}
                  limit={4}
                />
              </div>
              <div className={styles.row} key="top-bar-7">
                <DropdownQuantity
                  ariaLabel={Search.Children}
                  placeholder={Strings.search.zero}
                  key={SEARCH_BAR_FIELD.children}
                  name={SEARCH_BAR_FIELD.children}
                  onChange={onChildrenChange}
                  value={children.length}
                  label={Strings.search.children}
                  error={errors[SEARCH_BAR_FIELD.children] && Strings.cannotBeLeftBlank}
                  limit={4}
                />
              </div>
              {
                children.map((age, idx) => (
                  <div className={styles.row}>
                    <DropDown
                      ariaLabel={Search.ChildAge}
                      placeholder={Strings.search.age}
                      name={`${SEARCH_BAR_FIELD.children}-${idx}`}
                      options={ageOptions}
                      // eslint-disable-next-line react/jsx-no-bind
                      onChange={value => onChangeChildAge(idx, value)}
                      value={getOption(String(children[idx]).padStart(2, '0'), ageOptions)}
                      label={`${Strings.search.child} ${idx + 1} ${Strings.search.age}`}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${SEARCH_BAR_FIELD.chidren}-${idx}`}
                    />
                  </div>
                ))
              }
            </>
          )
        }
        { content === FILTERS && filters }
      </FullPageModal>
    </div>
  )
}

const stateProps = (state: StoreState) => ({
  // $FlowFixMe
  search: state.router.location.search || '',
  locations: selectors.apple.sortedDropDownLocations(state),
  properties: selectors.apple.getProperties(state),
})

const dispatchProps = (dispatch: Dispatch): DispatchToProps => ({
  fetchProperty: (data: SearchPayload) => dispatch(ActionCreators.apple.searchPropertiesRequest.dispatch(data)),
  setFilterProperty: (data: any) => dispatch(ActionCreators.apple.setFilterProperty.dispatch(data))
})

// $FlowFixMe
export default connect(stateProps, dispatchProps)(TopBar)
